import axios from "axios";
import { useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Table,
  Typography,
  Timeline,
  Row,
  Col,
  Select,
  Button,
  Tag,
  Input,
  Badge,
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import {
  ReloadOutlined,
  EyeOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import ProbationReviewModal from "./ProbationReviewModal";
import "../TeamLeaves.css";

const { Title } = Typography;
const { Option } = Select;

// TL/HOD/Approver view tabs:
// - Pending        = records where it's currently MY turn to approve (is_ready=true)
// - Confirmed      = records where MY approval step = Confirm
// - Extended       = records where MY approval step = Extend (now re-reviewing)
// - Separated      = records where MY approval step = Separated
// - Probation List = ALL probation employees in my team/dept (read-only visibility)
const STATUS_TABS = [
  { key: "Pending",        label: "Pending",        color: "#faad14" },
  { key: "Confirm",        label: "Confirmed",       color: "#4CAF50" },
  { key: "Extend",         label: "Extended",        color: "#0e497b" },
  { key: "Separated",      label: "Separated",       color: "#f5222d" },
  { key: "Probation List", label: "Probation List",  color: "#722ed1" },
];

const TAG_COLOR = {
  Pending:   "warning",
  Confirm:   "success",
  Extend:    "processing",
  Separated: "error",
};

const TAG_LABEL = {
  Pending:   "Pending",
  Confirm:   "Confirmed",
  Extend:    "Extended",
  Separated: "Separated",
};

export default function ProbationReview() {
  const { backUrl, authData } = useContext(AuthContext);

  const [employees, setEmployees]                 = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [activeTab, setActiveTab]                 = useState("Pending");

  const [tabCounts, setTabCounts] = useState({
    Pending: 0, Confirm: 0, Extend: 0, Separated: 0, "Probation List": 0,
  });

  const [searchName, setSearchName]       = useState("");
  const [searchEmpCode, setSearchEmpCode] = useState("");
  const [pagination, setPagination]       = useState({ current: 1, pageSize: 50 });

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal]               = useState(false);

  const [Organisations, setOrganisations]           = useState([]);
  const [departments, setDepartments]               = useState([]);
  const [designations, setDesignations]             = useState([]);
  const [organisationFilter, setOrganisationFilter] = useState("");
  const [departmentFilter, setDepartmentFilter]     = useState("");
  const [designationFilter, setDesignationFilter]   = useState("");

  const headers = { Authorization: `Bearer ${authData?.access}` };

  const buildUrl = useCallback(
    (statusKey) => {
      if (statusKey === "Probation List") {
        // Fetches the role-aware probation employees list (team/department filtered)
        return `${backUrl}/api/probation/probation-reviews/probation_employees/?is_employee_view=true`;
      }
      const params = new URLSearchParams();
      params.append("is_employee_view", "true");

      if (statusKey === "Pending") {
        params.append("overall_status", "Pending");
        params.append("is_ready", "true");
      } else {
        params.append("overall_status", statusKey);
      }

      return `${backUrl}/api/probation/probation-reviews/?${params.toString()}`;
    },
    [backUrl]
  );

  const fetchData = useCallback(
    async (tab) => {
      const tabKey = tab ?? activeTab;
      setLoading(true);
      try {
        const res = await axios.get(buildUrl(tabKey), { headers });
        setEmployees(res.data.results || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch probation reviews.");
      } finally {
        setLoading(false);
      }
    },
    [activeTab, buildUrl]
  );

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(
        `${backUrl}/api/probation/probation-reviews/counts/?is_employee_view=true`,
        { headers }
      );
      const d = res.data;
      setTabCounts({
        Pending:          d.Ready   ?? d.Pending   ?? 0,
        Confirm:          d.Confirmed ?? 0,
        Extend:           d.Extended  ?? 0,
        Separated:        d.Separated ?? 0,
        "Probation List": d.ProbationList ?? 0,
      });
    } catch (_) {
      console.error("Failed to fetch counts");
    }
  }, [backUrl]);

  const fetchOrganisation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, { headers });
      setOrganisations(res.data.results || []);
    } catch (_) {}
  };

  const fetchDepartments = async (orgName) => {
    const org = Organisations.find((o) => o.name === orgName);
    if (!org) return;
    const res = await axios.get(
      `${backUrl}/api/emp/departments/?organisation=${org.id}`,
      { headers }
    );
    setDepartments(res.data.results || []);
  };

  const fetchDesignations = async (deptName) => {
    const dept = departments.find((d) => d.name === deptName);
    if (!dept) return;
    const res = await axios.get(
      `${backUrl}/api/emp/designations/?department=${dept.id}`,
      { headers }
    );
    setDesignations(res.data.results || []);
  };

  useEffect(() => {
    fetchOrganisation();
    fetchData("Pending");
    fetchCounts();
  }, []);

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  // Client-side filtering
  useEffect(() => {
    let filtered = [...employees];

    if (organisationFilter)
      filtered = filtered.filter(
        (i) => i.employee?.organisation_name === organisationFilter
      );
    if (departmentFilter)
      filtered = filtered.filter(
        (i) => i.employee?.department_name === departmentFilter
      );
    if (designationFilter)
      filtered = filtered.filter(
        (i) => i.employee?.designation_name === designationFilter
      );
    if (searchEmpCode)
      filtered = filtered.filter((i) =>
        i.employee?.emp_code?.toLowerCase().includes(searchEmpCode.toLowerCase())
      );
    if (searchName)
      filtered = filtered.filter((i) =>
        `${i.employee?.first_name || ""} ${i.employee?.last_name || ""}`
          .toLowerCase()
          .includes(searchName.toLowerCase())
      );

    setFilteredEmployees(filtered);
  }, [
    organisationFilter, departmentFilter, designationFilter,
    searchEmpCode, searchName, employees,
  ]);

  const handleClearFilters = () => {
    setSearchName(""); setSearchEmpCode("");
    setOrganisationFilter(""); setDepartmentFilter(""); setDesignationFilter("");
    setDepartments([]); setDesignations([]);
  };

  // Approval Hierarchy column — TeamLeaves-style full timeline for Probation List tab
  const approvalChainColumn = {
    title: "Approval Hierarchy",
    key: "chain",
    width: 420,
    render: (_, record) => {
      const steps = record.approval_steps || [];
      if (!steps.length) return <span style={{ color: "#aaa" }}>—</span>;

      return (
        <div style={{ marginTop: 8 }}>
          <Timeline
            items={steps.map((step) => {
              const cleanStatus = (step.status || "").trim();
              const isSeparated   = cleanStatus === "Separated";
              const isConfirmed   = cleanStatus === "Confirm";
              const isExtended    = cleanStatus === "Extend";
              const isCurrentStep = step.is_current_step === true;
              const isNotReached  = cleanStatus === "Pending" && !isCurrentStep;

              const timelineColor =
                isSeparated ? "red"
                : isConfirmed ? "green"
                : isExtended  ? "blue"
                : isCurrentStep ? "#0e497b"
                : "gray";

              return {
                color: timelineColor,
                children: (
                  <div style={{ marginBottom: 6, opacity: isNotReached ? 0.5 : 1 }}>

                    {/* Row 1: Step + Name + Status Tag */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        Step {step.order}: {step.approver_name}
                      </Typography.Text>
                      {isCurrentStep ? (
                        <Tag color="processing" style={{ margin: 0, fontSize: 11 }}>Awaiting</Tag>
                      ) : isNotReached ? (
                        <Tag color="default" style={{ margin: 0, fontSize: 11 }}>Not Reached</Tag>
                      ) : (
                        <Tag
                          color={
                            isSeparated ? "error"
                            : isConfirmed ? "success"
                            : isExtended  ? "processing"
                            : "default"
                          }
                          style={{ margin: 0, fontSize: 11 }}
                        >
                          {TAG_LABEL[cleanStatus] || cleanStatus}
                        </Tag>
                      )}
                    </div>

                    {/* Row 2: Designation + Emp Code */}
                    {step.approver_designation && (
                      <div style={{ color: "#8c8c8c", fontSize: 11, marginTop: 2 }}>
                        {step.approver_designation}
                        {step.approver_emp_code && ` • ${step.approver_emp_code}`}
                      </div>
                    )}

                    {/* Row 3: Action Date */}
                    {step.completed_on && (
                      <div style={{ color: "#595959", fontSize: 11, marginTop: 3 }}>
                        <Typography.Text strong style={{ fontSize: 10, color: "#595959" }}>
                          {isSeparated ? "Separated On: "
                           : isExtended ? "Extended On: "
                           : "Confirmed On: "}
                        </Typography.Text>
                        {dayjs(step.completed_on).format("DD MMM YYYY, hh:mm A")}
                      </div>
                    )}

                    {/* Row 4: Remarks */}
                    {step.remarks && (
                      <div style={{ marginTop: 3, fontSize: 12 }}>
                        <Typography.Text strong>Remark: </Typography.Text>
                        <Typography.Text>{step.remarks}</Typography.Text>
                      </div>
                    )}

                    {/* Row 5: Extended To Date */}
                    {step.new_probation_end_date && (
                      <div style={{ marginTop: 3, fontSize: 12 }}>
                        <Typography.Text strong style={{ color: "#1890ff" }}>Extended to: </Typography.Text>
                        <Typography.Text style={{ color: "#1890ff" }}>
                          {new Date(step.new_probation_end_date).toLocaleDateString("en-GB")}
                        </Typography.Text>
                      </div>
                    )}

                  </div>
                ),
              };
            })}
          />
        </div>
      );
    },
  };

  const baseColumns = [
    {
      title: "Emp Code",
      dataIndex: ["employee", "emp_code"],
      key: "emp_code",
      width: 120,
      fixed: "left",
      render: (text, record) => (
        <a
          onClick={() => { setSelectedEmployee(record); setShowModal(true); }}
          style={{ cursor: "pointer", fontWeight: 600, color: "#0e497b" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Employee Name",
      key: "employee_name",
      width: 200,
      render: (_, r) =>
        `${r.employee?.first_name || ""} ${r.employee?.last_name || ""}`.trim() || "—",
    },
    {
      title: "Date of Joining",
      dataIndex: ["employee", "date_of_joining"],
      key: "doj",
      render: (t) => (t ? new Date(t).toLocaleDateString("en-GB") : "—"),
    },
    {
      title: "Probation End Date",
      dataIndex: "final_probation_end_date",
      key: "prob_end",
      render: (t) => (t ? new Date(t).toLocaleDateString("en-GB") : "—"),
    },
    {
      title: "Overall Status",
      dataIndex: "overall_status",
      key: "status",
      render: (s) => {
        const cleanStatus = (s || "").trim();
        return <Tag color={TAG_COLOR[cleanStatus] || "default"}>{TAG_LABEL[cleanStatus] || cleanStatus}</Tag>;
      },
    },
    {
      title: "Department",
      dataIndex: ["employee", "department_name"],
      key: "department",
    },
    {
      title: "Designation",
      dataIndex: ["employee", "designation_name"],
      key: "designation",
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", borderRadius: 4 }}
          onClick={() => { setSelectedEmployee(record); setShowModal(true); }}
        >
          {activeTab === "Probation List" ? "View" : "Review"}
        </Button>
      ),
    },
  ];

  // Do not show Approval Hierarchy column here, since it is shown in the modal
  const columns = [
    ...baseColumns.slice(0, 5),  // Emp Code, Name, DOJ, End Date, Status
    baseColumns[5],              // Department
    baseColumns[6],              // Designation
    baseColumns[7],              // Action
  ];


  return (
    <div style={{ padding: "20px" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <center>
        <Title level={5} style={{ color: "#0e497b", textTransform: "uppercase" }}>
          Probation Reviews
        </Title>
      </center>

      {/* Status Tab Buttons with Badge count */}
      <div
        style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          marginBottom: "20px", gap: "28px", flexWrap: "wrap",
        }}
      >
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const count    = tabCounts[tab.key] ?? 0;
          return (
            <Badge
              key={tab.key}
              count={count}
              overflowCount={999}
              style={{
                backgroundColor: isActive ? tab.color : "#d9d9d9",
                color:           isActive ? "#fff" : "#595959",
                fontWeight:      600,
                boxShadow:       "none",
              }}
            >
              <Button
                type={isActive ? "primary" : "default"}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  backgroundColor: isActive ? tab.color : undefined,
                  borderColor:     isActive ? tab.color : undefined,
                  minWidth:        120,
                }}
              >
                {isActive && loading ? (
                  <><LoadingOutlined style={{ marginRight: 6 }} />{tab.label}</>
                ) : (
                  tab.label
                )}
              </Button>
            </Badge>
          );
        })}
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => { fetchData(activeTab); fetchCounts(); }}
        >
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <Row gutter={[12, 12]} justify="center">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder="Emp Code"
              value={searchEmpCode}
              onChange={(e) => setSearchEmpCode(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder="Employee Name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              placeholder="Select Organisation"
              style={{ width: "100%" }}
              value={organisationFilter || undefined}
              onChange={(val) => {
                setOrganisationFilter(val);
                setDepartmentFilter(""); setDesignationFilter("");
                setDepartments([]); setDesignations([]);
                if (val) fetchDepartments(val);
              }}
              allowClear
            >
              {Organisations.map((o) => (
                <Option key={o.id} value={o.name}>{o.name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              showSearch
              placeholder="Select Department"
              style={{ width: "100%" }}
              value={departmentFilter || undefined}
              onChange={(val) => {
                setDepartmentFilter(val); setDesignationFilter(""); setDesignations([]);
                if (val) fetchDesignations(val);
              }}
              allowClear
              disabled={!organisationFilter}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {departments.map((d) => (
                <Option key={d.id} value={d.name}>{d.name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              showSearch
              placeholder="Select Designation"
              style={{ width: "100%" }}
              value={designationFilter || undefined}
              onChange={(val) => setDesignationFilter(val)}
              allowClear
              disabled={!departmentFilter}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {designations.map((d) => (
                <Option key={d.id} value={d.name}>{d.name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Button
              block
              type="primary"
              style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table
          columns={columns}
          dataSource={filteredEmployees}
          loading={loading}
          pagination={{
            current:         pagination.current,
            pageSize:        pagination.pageSize,
            total:           filteredEmployees.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            onChange:        (p, s) => setPagination({ current: p, pageSize: s }),
            showTotal:       (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          }}
          rowKey={(r) => r.id}
          bordered
          size="middle"
          scroll={{ x: "max-content" }}
          components={{
            header: {
              cell: (props) => (
                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                  {props.children}
                </th>
              ),
            },
          }}
        />
      </div>


      {showModal && selectedEmployee && (
        <ProbationReviewModal
          visible={showModal}
          employee={selectedEmployee}
          onClose={() => {
            setShowModal(false);
            fetchData(activeTab);
            fetchCounts();
          }}
        />
      )}
    </div>
  );
}
